import JSZip from 'jszip';

import randId from './id.ts';
import { db, Song, Songbook, SongMedia } from './db';

interface DexieManifest {
  app: string;
  kind: string;
  format: string;
  version: number;
  exportedAt?: string;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function createExportDownload(songbooks: Songbook[], songs: Song[], media: (SongMedia)[]) {
  const manifest: DexieManifest = {
    app: 'ChordMage',
    kind: 'backup',
    format: 'chord-mage-import',
    version: 1,
    exportedAt: new Date().toISOString(),
  };

  const zip = new JSZip();

  zip.file('manifest.json', JSON.stringify(manifest, null, 2));
  zip.file('songbooks.json', JSON.stringify(songbooks, null, 2));
  zip.file('songs.json', JSON.stringify(songs, null, 2));
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  zip.file('media.json', JSON.stringify(media.map(({ contents, ...m }) => m), null, 2));

  const mediaFolder = zip.folder('media');

  if (mediaFolder) {
    media.forEach(({ id, contents }) => {
      mediaFolder.file(id, contents);
    });
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });

  const timeStamp = new Date()
    .toISOString()
    .replace('T', '_')
    .replace(':', '')
    .substring(0, 15);

  const filename = `ChordMage-backup-${timeStamp}.zip`;

  downloadBlob(zipBlob, filename);
}

interface ExportDbOptions {
  songbookId?: string[];
  songId?: string[];
}

async function createExportFiles(options: ExportDbOptions): Promise<[Songbook[], Song[], SongMedia[]]> {
  const { songbookId, songId } = options;

  let songs: Song[] = [];
  let songbooks: Songbook[] = [];

  if (songId) {
    songs = await db.songs.where('id').anyOf(songId).toArray();

    const songbookIds = songs.map((s) => s.songbookId);

    songbooks = await db.songbooks.where('id').anyOf(songbookIds).toArray();
  } else {
    const songbookPromise = songbookId
      ? db.songbooks.where('id').anyOf(songbookId)
      : db.songbooks;
    const songsPromise = songbookId
      ? db.songs.where('songbookId').anyOf(songbookId)
      : db.songs;

    [songbooks, songs] = await Promise.all([
      songbookPromise.toArray(),
      songsPromise.toArray(),
    ]);
  }


  const media = await db.media
    .where('songId')
    .anyOf(songs.map((s) => s.id))
    .toArray();

  return [
    songbooks,
    songs,
    media,
  ];
}

export async function exportDbToZip(options: ExportDbOptions = {}) {
  const [songbooks, songs, media] = await createExportFiles(options);

  await createExportDownload(songbooks, songs, media);
}

export async function readImportZip(file: File) {
  const zip = await JSZip.loadAsync(file);

  const manifestEntry = zip.file('manifest.json');

  if (!manifestEntry) {
    throw new Error('Invalid backup: required files missing.');
  }

  const manifestText = await manifestEntry.async('text');
  const manifest: DexieManifest = JSON.parse(manifestText);

  if (manifest.app !== 'ChordMage' || manifest.kind !== 'backup') {
    throw new Error('Not a ChordMage backup.');
  }

  if (!manifest.format?.startsWith('chord-mage-import')) {
    throw new Error('Unsupported backup format.');
  }

  const songbooks: Songbook[] = await zip.file('songbooks.json')?.async('text').then(JSON.parse);
  const songs: Song[] = await zip.file('songs.json')?.async('text').then(JSON.parse);
  const media: SongMedia[] = await zip.file('media.json')?.async('text').then(JSON.parse);

  const mediaFolder = zip.folder('media');

  if (!mediaFolder) {
    throw new Error('Media folder not found.');
  }

  return { songbooks, songs, media, mediaFolder };
}

export interface ImportData {
  songbooks: Songbook[];
  songs: Song[];
  media: SongMedia[];
  mediaFolder: JSZip;
}

export async function addImportToDb({ songbooks, songs, media, mediaFolder }: ImportData) {
  await Promise.all(songbooks.map((s) => {
    return db.songbooks.upsert(s.id, s);
  }));
  await Promise.all(songs.map((s) => {
    return db.songs.upsert(s.id, s);
  }));
  await Promise.all(media.map(async (s) => {
    const contents = await mediaFolder.file(s.originalId || s.id)?.async('arraybuffer');

    return db.media.upsert(s.id, { ...s, contents });
  }));
}

export function moveSongsToSongbook(data: ImportData, importToId: string): ImportData {
  const newData = {
    ...data,
    songbooks: [],
    songs: data.songs.map((s) => ({
      ...s,
      id: randId(),
      songbookId: importToId,
    })),
  };

  // set new ids in songs
  newData.media = newData.media
    .map((m) => ({
      ...m,
      id: randId(),
      originalId: m.id,
      songId: newData.songs[data.songs.findIndex((s) => s.id === m.songId)].id,
    }));

  return newData;
}
